import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import { API_ENDPOINTS } from "../../../api/header";
import apiClient from "../../../api/apiClient";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusCircle,
  MoreVertical,
  Trash2,
  Edit,
  Eye,
  Loader2,
  CheckCircle,
  Archive,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";

const AssembleConfigsList = () => {
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => permissions.includes(permission);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewConfigId, setViewConfigId] = useState(null);
  const [viewConfigBasic, setViewConfigBasic] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {},
    variant: "destructive",
    confirmText: "Confirm",
  });

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "$";

  const { data: configsData, isLoading } = useQuery({
    queryKey: ["assemble-configs", currentPage, pageSize, searchText, store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.ASSEMBLE.CONFIGS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || undefined,
          store_id: store_id,
        },
      });
      return res.data;
    },
  });

  const { data: configDetailsData, isLoading: isConfigDetailsLoading } =
    useQuery({
      queryKey: ["assemble-config", viewConfigId],
      queryFn: async () => {
        const res = await apiClient.get(
          API_ENDPOINTS.ASSEMBLE.CONFIG_DETAILS(viewConfigId),
        );
        return res.data?.data || res.data;
      },
      enabled: !!viewConfigId,
    });
  const configDetails = useMemo(() => configDetailsData, [configDetailsData]);

  const configs = useMemo(() => configsData?.data || [], [configsData]);

  const paginationMeta = {
    current_page: configsData?.meta?.current_page || 1,
    last_page: configsData?.meta?.last_page || 1,
    per_page: configsData?.meta?.per_page || 15,
    total: configsData?.meta?.total || 0,
  };

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(API_ENDPOINTS.ASSEMBLE.CONFIG_DETAILS(id));
    },
    onSuccess: () => {
      toast.success("Assemble config deleted successfully");
      queryClient.invalidateQueries(["assemble-configs"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete config");
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.post(API_ENDPOINTS.ASSEMBLE.CONFIG_ACTIVATE(id));
    },
    onSuccess: () => {
      toast.success("Assemble config activated successfully");
      queryClient.invalidateQueries(["assemble-configs"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to activate config");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.post(API_ENDPOINTS.ASSEMBLE.CONFIG_ARCHIVE(id));
    },
    onSuccess: () => {
      toast.success("Assemble config archived successfully");
      queryClient.invalidateQueries(["assemble-configs"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to archive config");
    },
  });

  const handleViewConfig = (config) => {
    setViewConfigId(config.id);
    setViewConfigBasic(config);
    setViewModalOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Config",
      description:
        "Are you sure you want to delete this config? This action cannot be undone.",
      variant: "destructive",
      confirmText: "Delete",
      onConfirm: () => deleteMutation.mutate(id),
    });
  };

  const handleActivate = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Activate Config",
      description: "Are you sure you want to activate this config?",
      variant: "default",
      confirmText: "Activate",
      onConfirm: () => activateMutation.mutate(id),
    });
  };

  const handleArchive = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Archive Config",
      description: "Are you sure you want to archive this config?",
      variant: "default",
      confirmText: "Archive",
      onConfirm: () => archiveMutation.mutate(id),
    });
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Config Name",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: "base_price",
      header: "Base Price",
      cell: ({ row }) => (
        <span>${parseFloat(row.original.base_price || 0).toFixed(2)}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const statusValue = row.original.status?.value || "active";
        const statusLabel = row.original.status?.label || "Active";
        return (
          <Badge variant={statusValue === "active" ? "success" : "secondary"}>
            {statusLabel}
          </Badge>
        );
      },
    },
    {
      accessorKey: "show_component_prices",
      header: "Show Prices",
      cell: ({ row }) => (
        <Badge
          variant={row.original.show_component_prices ? "success" : "secondary"}
        >
          {row.original.show_component_prices ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return formatDate(date);
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const id = row.original.id;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleViewConfig(row.original)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {row.original.status?.value !== "active" &&
                hasPermission("assemble config update") && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => handleActivate(id)}
                    disabled={activateMutation.isPending}
                  >
                    <CheckCircle className="mr-2 h-4 w-4 text-green-600" />{" "}
                    Activate
                  </DropdownMenuItem>
                )}
              {row.original.status?.value !== "archived" &&
                hasPermission("assemble config update") && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => handleArchive(id)}
                    disabled={archiveMutation.isPending}
                  >
                    <Archive className="mr-2 h-4 w-4" /> Archive
                  </DropdownMenuItem>
                )}
              {hasPermission("assemble config update") && (
                <Link
                  to={(
                    all_routes.editAssembleConfig || "/edit-assemble-config/:id"
                  ).replace(":id", id)}
                >
                  <DropdownMenuItem>
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                </Link>
              )}
              {hasPermission("assemble config delete") && (
                <DropdownMenuItem
                  className="text-destructive cursor-pointer"
                  onClick={() => handleDelete(id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Assemble Configs"
          subtitle="Manage PC assembly configurations"
        />
        <PageHeaderActions>
          {hasPermission("assemble config create") && (
            <Link to={all_routes.addAssembleConfig}>
              <Button>
                <PlusCircle className="mr-2 h-4 w-4" /> Add Assemble PC
              </Button>
            </Link>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search configs..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={configs}
          onRowClick={handleViewConfig}
          loading={isLoading}
          pagination={paginationMeta}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </TableCard>

      <Dialog
        open={viewModalOpen}
        onOpenChange={(open) => {
          setViewModalOpen(open);
          if (!open) {
            setViewConfigId(null);
            setViewConfigBasic(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-muted-foreground" />
              Config Details - {viewConfigBasic?.name}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 px-6 py-4">
            {isConfigDetailsLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : configDetails ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Configuration Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Config Name:
                        </span>
                        <span className="font-medium">
                          {configDetails.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge
                          variant={
                            configDetails.status?.value === "active"
                              ? "success"
                              : "secondary"
                          }
                        >
                          {configDetails.status?.label || "Active"}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Base Price:
                        </span>
                        <span className="font-medium">
                          {currencySymbol}
                          {parseFloat(configDetails.base_price || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Created Date:
                        </span>
                        <span className="font-medium">
                          {formatDate(configDetails.created_at,)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {configDetails.items && configDetails.items.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Components</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border overflow-x-auto">
                        <ShadcnTable>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">#</TableHead>
                              <TableHead>Product</TableHead>
                              <TableHead className="text-right">Qty</TableHead>
                              <TableHead className="text-center">
                                Optional
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {configDetails.items.map((item, index) => {
                              return (
                                <TableRow key={item.id || index}>
                                  <TableCell className="font-medium">
                                    {index + 1}
                                  </TableCell>
                                  <TableCell>
                                    <div className="font-medium">
                                      {item.product?.name ||
                                        item.product_data?.name ||
                                        "Unknown Product"}
                                    </div>
                                    {item.product?.sku && (
                                      <div className="text-[11px] text-muted-foreground mt-0.5">
                                        SKU: {item.product.sku}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge
                                      variant={
                                        item.is_optional
                                          ? "secondary"
                                          : "outline"
                                      }
                                    >
                                      {item.is_optional ? "Yes" : "No"}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </ShadcnTable>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : null}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() =>
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
              }
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmDialog.variant === "destructive"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
              }}
            >
              {confirmDialog.confirmText || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default AssembleConfigsList;
